import { useEffect, useState } from 'react'
import { Box, Flex, Text, VStack, Grid, Input, Spinner, ConfirmDialog, useT } from '@minetrace/ui'
import {
  LuPlus as Plus,
  LuTrash2 as Trash2,
  LuBuilding as Building,
} from 'react-icons/lu'
import { useForm, Controller, useFormColors, FormSection, StyledSelect } from '@minetrace/ui/forms'
import { FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toaster } from '@minetrace/ui'
import { BANK_ENTITIES, ACCOUNT_TYPES, INCOME_TAX_CATEGORIES, BANCO_NACION_ID } from '../data/enums'
import CharCounter from './CharCounter'
import { BankAccountFieldLengths } from '../lib/fieldLengths'
import { bankAccountNumber } from '../lib/validators'

const DETRACCION_ACCOUNT_TYPE_ID = 2
import type { BankAccountResponse } from '../api/models/response/proveedor'

const bankAccountSchema = z.object({
  accountNumber: bankAccountNumber(),
  bankEntityId: z.number().min(1, 'shared.taxInfo.zodBankEntityRequired'),
  accountTypeId: z.number().min(1, 'shared.taxInfo.zodAccountTypeRequired'),
})
type BankAccountFormData = z.infer<typeof bankAccountSchema>

interface TaxInfoSectionProps {
  /** Cuando es 'readonly' se oculta el form de alta de cuentas, los botones de
   *  borrado, y el select de categoría queda deshabilitado. */
  mode?: 'edit' | 'readonly'
  bankAccounts: BankAccountResponse[]
  incomeTaxCategoryId: number
  onIncomeTaxChange: (value: number) => void
  onAddAccount: (data: { accountNumber: string; bankEntityId: number; accountTypeId: number }) => Promise<BankAccountResponse>
  onRemoveAccount: (accountId: string) => Promise<void>
}

const TaxInfoSection = ({ mode = 'edit', bankAccounts, incomeTaxCategoryId, onIncomeTaxChange, onAddAccount, onRemoveAccount }: TaxInfoSectionProps) => {
  const isReadonly = mode === 'readonly'
  const c = useFormColors()
  const { t } = useT()
  const [adding, setAdding] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; number: string } | null>(null)
  const [deleting, setDeleting] = useState(false)

  const formMethods = useForm<BankAccountFormData>({
    resolver: zodResolver(bankAccountSchema),
    defaultValues: {
      accountNumber: '',
      bankEntityId: 0,
      accountTypeId: 0,
    },
  })
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = formMethods

  // Detracción accounts are always issued by Banco de la Nación — auto-fill + lock.
  const watchedAccountTypeId = watch('accountTypeId')
  const isDetraccion = watchedAccountTypeId === DETRACCION_ACCOUNT_TYPE_ID
  useEffect(() => {
    if (isDetraccion) setValue('bankEntityId', BANCO_NACION_ID)
  }, [isDetraccion, setValue])

  const inputStyles = {
    bg: c.bgInput,
    border: '1px solid',
    borderColor: 'gray.200',
    borderRadius: 'lg',
    color: c.text,
    h: '42px',
    fontSize: 'sm',
    _placeholder: { color: c.textMuted },
    _focus: { outline: 'none', borderColor: '#b4532a', ring: '1px', ringColor: '#b4532a' },
  } as const

  const onSubmit = async (data: BankAccountFormData) => {
    setAdding(true)
    try {
      await onAddAccount({ accountNumber: data.accountNumber.trim(), bankEntityId: data.bankEntityId, accountTypeId: data.accountTypeId })
      reset({ accountNumber: '', bankEntityId: 0, accountTypeId: 0 })
      toaster.success({ description: t('shared.taxInfo.addedToast') })
    } finally {
      setAdding(false)
    }
  }

  return (
    <FormProvider {...formMethods}>
    <VStack gap={6} alignItems="stretch">
      <FormSection icon={Building} titleTid="shared.taxInfo.title" descriptionTid="shared.taxInfo.description">
        {/* Income Tax Category */}
        <Box mb={5}>
          <Text tid="shared.taxInfo.incomeTaxLabel" fontSize="xs" fontWeight="600" color={c.textMuted} textTransform="uppercase" letterSpacing="0.03em" mb={1} />
          <Box maxW="400px">
            {isReadonly ? (
              // En revision no va un desplegable deshabilitado: cuando el dato no esta
              // cargado mostraba "Seleccionar...", que se lee como un control roto y no
              // como "este minero no informo la categoria".
              <Text fontSize="sm" fontWeight="500" color={incomeTaxCategoryId ? c.text : c.textMuted}>
                {INCOME_TAX_CATEGORIES.find(cat => cat.value === incomeTaxCategoryId && cat.value !== 0)?.label
                  ?? 'No informado'}
              </Text>
            ) : (
              <StyledSelect value={incomeTaxCategoryId} onChange={(e) => onIncomeTaxChange(parseInt(e.target.value as string))}>
                {INCOME_TAX_CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </StyledSelect>
            )}
          </Box>
        </Box>

        {/* Bank Accounts */}
        <Text tid="shared.taxInfo.bankAccountsTitle" fontSize="sm" fontWeight="700" color={c.text} mb={3} />

        {/* Add form */}
        {!isReadonly && (
        <Box bg={c.bgHover} borderRadius="xl" p={4} mb={4}>
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr 1fr auto' }} gap={3} alignItems="flex-end">
            <Box>
              <Text tid="shared.taxInfo.accountTypeLabel" fontSize="10px" fontWeight="600" color="#b5aa9d" textTransform="uppercase" mb={1} />
              <Controller
                name="accountTypeId"
                control={control}
                render={({ field }) => (
                  <StyledSelect value={field.value} onChange={(e) => field.onChange(parseInt(e.target.value as string))}>
                    {ACCOUNT_TYPES.map((tp) => (
                      <option key={tp.value} value={tp.value} disabled={tp.value === 0}>{tp.label}</option>
                    ))}
                  </StyledSelect>
                )}
              />
              {errors.accountTypeId && (
                <Text fontSize="xs" color={c.error} mt={1}>{t(errors.accountTypeId.message ?? '')}</Text>
              )}
            </Box>
            <Box>
              <Text tid="shared.taxInfo.bankEntityLabel" fontSize="10px" fontWeight="600" color="#b5aa9d" textTransform="uppercase" mb={1} />
              <Controller
                name="bankEntityId"
                control={control}
                render={({ field }) => (
                  <StyledSelect
                    value={field.value}
                    onChange={(e) => field.onChange(parseInt(e.target.value as string))}
                    disabled={isDetraccion}
                    title={isDetraccion ? 'Las cuentas de detracción se abren automáticamente en Banco de la Nación' : undefined}
                  >
                    {BANK_ENTITIES.map((b) => (
                      <option key={b.value} value={b.value} disabled={b.value === 0}>{b.label}</option>
                    ))}
                  </StyledSelect>
                )}
              />
              {errors.bankEntityId && (
                <Text fontSize="xs" color={c.error} mt={1}>{t(errors.bankEntityId.message ?? '')}</Text>
              )}
            </Box>
            <Box>
              <Text tid="shared.taxInfo.accountNumberLabel" fontSize="10px" fontWeight="600" color="#b5aa9d" textTransform="uppercase" mb={1} />
              <Input
                {...register('accountNumber')}
                placeholder={t('shared.taxInfo.accountNumberPlaceholder')}
                maxLength={BankAccountFieldLengths.accountNumber}
                fontFamily="monospace"
                {...inputStyles}
              />
              <Flex justifyContent="space-between" alignItems="center" mt={1}>
                {errors.accountNumber ? (
                  <Text fontSize="xs" color={c.error}>{t(errors.accountNumber.message ?? '')}</Text>
                ) : <span />}
                <Box ml="auto"><CharCounter name="accountNumber" max={BankAccountFieldLengths.accountNumber} mt={0} /></Box>
              </Flex>
            </Box>
            <Box
              as="button" type="button" display="flex" alignItems="center" gap={2}
              px={4} h="42px" bg="#b4532a" color="white" borderRadius="lg"
              fontSize="sm" fontWeight="600" cursor={adding ? 'not-allowed' : 'pointer'}
              opacity={adding ? 0.6 : 1} _hover={{ bg: '#085a30' }} whiteSpace="nowrap"
              onClick={handleSubmit(onSubmit)} disabled={adding}
            >
              {adding ? <Spinner size="sm" /> : <Plus size={16} />}
              {t('shared.taxInfo.addButton')}
            </Box>
          </Grid>
        </Box>
        )}

        {/* Accounts list */}
        {bankAccounts.length === 0 ? (
          <Box p={4} textAlign="center" border="1px dashed" borderColor="gray.200" borderRadius="xl">
            <Text tid="shared.taxInfo.emptyMessage" fontSize="sm" color={c.textMuted} />
          </Box>
        ) : (
          <VStack gap={2} alignItems="stretch">
            {bankAccounts.map((acc) => (
              <Flex
                key={acc.id}
                bg="white"
                border="1px solid"
                borderColor="gray.100"
                borderRadius="lg"
                p={3}
                alignItems="center"
                gap={3}
                shadow="0 1px 2px rgba(0,0,0,0.04)"
              >
                <Box flex={1} minW={0}>
                  <Flex gap={2} alignItems="center" mb={1}>
                    <Text fontSize="sm" fontWeight="600" color={c.text} fontFamily="monospace">{acc.accountNumber}</Text>
                    <Box px={2} py={0.5} borderRadius="md" fontSize="10px" fontWeight="700" bg="#dcefed" color="#1d6664">
                      {acc.accountTypeName}
                    </Box>
                  </Flex>
                  <Text fontSize="xs" color={c.textMuted}>{acc.bankEntityName}</Text>
                </Box>
                {!isReadonly && (
                <Box
                  as="button" type="button" p={2} borderRadius="md" color="#b23a26" cursor="pointer"
                  _hover={{ bg: '#fce4e4' }}
                  onClick={() => setDeleteTarget({ id: acc.id, number: acc.accountNumber })}
                >
                  <Trash2 size={14} />
                </Box>
                )}
              </Flex>
            ))}
          </VStack>
        )}
      </FormSection>
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget) return
          setDeleting(true)
          try { await onRemoveAccount(deleteTarget.id); setDeleteTarget(null) } finally { setDeleting(false) }
        }}
        title={t('shared.taxInfo.deleteTitle')}
        confirmLabel={t('shared.taxInfo.deleteConfirm')}
        variant="danger"
        isLoading={deleting}
      >
        {t('shared.taxInfo.deleteQuestion')} <Text as="span" fontWeight="600" fontFamily="monospace">{deleteTarget?.number}</Text>?
      </ConfirmDialog>
    </VStack>
    </FormProvider>
  )
}

export default TaxInfoSection
